import { Plus, Pencil, Trash2, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DepartmentUsersTable } from './DepartmentUsersTable'
import { useDepartmentUsers } from '@/hooks/useDepartments'
import type { ApiDepartment } from '@/types/department'

interface DepartmentDetailProps {
	department: ApiDepartment | null
	onEdit: (dept: ApiDepartment) => void
	onDelete: (dept: ApiDepartment) => void
	onAddUser: () => void
}

export function DepartmentDetail({
	department,
	onEdit,
	onDelete,
	onAddUser,
}: DepartmentDetailProps) {
	const { data: users } = useDepartmentUsers(department?.id)

	if (!department) {
		return (
			<div className="flex-1 flex items-center justify-center bg-muted/20 hidden lg:flex">
				<div className="text-center">
					<Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
					<p className="text-muted-foreground">请从上方选择一个部门</p>
				</div>
			</div>
		)
	}

	return (
		<div className="flex-1 flex flex-col overflow-hidden min-h-0">
			{/* 部门头部 */}
			<div className="p-4 lg:p-6 border-b bg-background">
				<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
					<div>
						<h1 className="text-xl lg:text-2xl font-bold">{department.name}</h1>
						<p className="text-muted-foreground mt-1 flex items-center gap-2 text-sm">
							<Users className="h-4 w-4" />
							{department.children_count || 0} 个子部门 · {department.user_count || 0} 人
						</p>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="outline" size="sm" onClick={() => onEdit(department)}>
							<Pencil className="mr-1.5 h-4 w-4" />
							编辑
						</Button>
						<Button
							variant="outline"
							size="sm"
							className="text-destructive hover:text-destructive"
							onClick={() => onDelete(department)}
						>
							<Trash2 className="mr-1.5 h-4 w-4" />
							删除
						</Button>
					</div>
				</div>
			</div>

			{/* 部门说明 */}
			{department.description && (
				<div className="px-4 lg:px-6 py-3 bg-muted/50 border-b">
					<span className="text-sm text-muted-foreground">{department.description}</span>
				</div>
			)}

			{/* 成员列表 */}
			<div className="flex-1 overflow-auto p-4 lg:p-6">
				<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
					<h3 className="font-semibold">
						部门成员
						{users && <span className="text-muted-foreground font-normal ml-1">({users.length}人)</span>}
					</h3>
					<Button size="sm" onClick={onAddUser}>
						<Plus className="mr-1.5 h-4 w-4" />
						添加成员
					</Button>
				</div>
				<DepartmentUsersTable departmentId={department.id} onAddUser={onAddUser} />
			</div>
		</div>
	)
}
